import React, { useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BypassLayout } from '../components/common';
import ProjectListPanel from '../components/OcrHub/ProjectListPanel';
import ProjectDetailPanel from '../components/OcrHub/ProjectDetailPanel';
import { OCR_REVIEW_PARAM, readOcrReviewParam } from '../components/OcrHub/ocrReviewLink';

/**
 * 「OCR」 통합 페이지 (/ocr-hub) — FE-1 골격 + FE-2 생성·업로드·처리.
 *
 * 책 PDF(문제/정답/풀이)를 프로젝트 단위로 업로드 → LLM OCR → 검수 → 문제은행 승격.
 * ProblemHub/StudentsHub와 동일한 마스터-디테일 구조(임시 병행 운영, 기존 화면 무수정).
 * - 좌측(마스터): ProjectListPanel — OCR 프로젝트 목록 + 새 프로젝트 생성 모달
 * - 우측(디테일): ProjectDetailPanel — 파일 목록·업로드·처리 실행·OCR 진행률(검수 패널은 FE-4)
 *
 * 상태 A(미선택): 좌측 full-width
 * 상태 B(선택, 데스크톱): 좌측 compact(clamp 360~440px) + 우측 디테일
 * 모바일(선택): 목록 ↔ 디테일 풀스크린 토글
 *
 * 선택 상태는 URL param(/ocr-hub/project/:projectId), key={projectId} remount → stale 상태 방지.
 * `?review={ocr_prob_id}` 가 붙어 오면 그 문제의 검수 모달을 연다(문제 v2 「OCR 등록」 배지를 ⌘·Ctrl 로 눌러 새 탭에
 * 연 길 — ocrReviewLink). 연 뒤에는 주소에서 지운다(replace) — 모달을 닫고 새로고침해도 다시 열리지 않는다.
 */

function OcrHub() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const isSelected = !!projectId;
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewId = readOcrReviewParam(searchParams);

  const handleReviewOpened = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(OCR_REVIEW_PARAM);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleSelect = (id) => {
    if (id == null) return;
    navigate(`/ocr-hub/project/${id}`);
  };

  const handleCloseDetail = () => navigate('/ocr-hub');

  const renderDetail = () => (
    <ProjectDetailPanel
      key={projectId}
      projectId={projectId}
      onClose={handleCloseDetail}
      reviewId={reviewId}
      onReviewOpened={handleReviewOpened}
    />
  );

  return (
    <BypassLayout
      renderContent={({ isMobileView }) => {
        // 모바일: 선택 시 디테일 풀스크린, 아니면 목록 (단일 컬럼 토글)
        if (isMobileView) {
          return (
            <div
              className="flex flex-1 flex-col"
              style={{ width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' }}
            >
              {isSelected ? renderDetail() : (
                <ProjectListPanel selected={null} onSelect={handleSelect} />
              )}
            </div>
          );
        }

        // 데스크톱: 선택 시 좌우 분할(좌 clamp 360~440px + 우 디테일), 미선택 시 좌측 full-width
        const split = isSelected;
        return (
          <div className="flex h-[calc(100vh-6rem)] w-full gap-4">
            {/* 좌측: 프로젝트 목록 (마스터) — compact 폭은 ProblemHub/StudentsHub와 동일(형제 패턴) */}
            <div
              className={split ? 'h-full shrink-0' : 'h-full w-full min-w-0 flex-1'}
              style={split ? { width: 'clamp(360px, 40%, 440px)', transition: 'width 300ms ease' } : { transition: 'width 300ms ease' }}
            >
              <ProjectListPanel compact={split} selected={projectId} onSelect={handleSelect} />
            </div>

            {/* 우측: 디테일 */}
            {split && (
              <div className="h-full min-w-0 flex-1">
                {renderDetail()}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}

export default OcrHub;
